import React, { useState, useEffect } from "react";
import { useMutation } from "graphql-hooks";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, TextField, Button, Grid } from "@mui/material";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import AlertPopup from "../components/AlertPopup";

import { CONTACT_EMAIL_MUTATION } from "../graphQL/contact_qry";

const Contact = () => {
  const navigate = useNavigate();
  const [sendContactEml] = useMutation(CONTACT_EMAIL_MUTATION);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [showAlert, setShowAlert] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    let hasError = false;
    const errors = { name: "", email: "", phone: "", message: "" };

    if (!formData.name.trim()) {
      errors.name = "Please enter your name.";
      hasError = true;
    }

    if (!formData.email.trim()) {
      errors.email = "Please enter your email address.";
      hasError = true;
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
      hasError = true;
    }

    if (!formData.message.trim()) {
      errors.message = "Please enter a message.";
      hasError = true;
    }

    if (hasError) {
      setFormErrors(errors);
      return;
    }

    // Submit the form via API
    const { data: sendEmlData, error: sendEmlError } = await sendContactEml({
      variables: {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        phone: formData.phone,
      },
    });
    if (sendEmlError) {
      console.log(sendEmlError);
    } else {
      setShowAlert(true);
    }
  };

  const handleAlertClose = () => {
    setShowAlert(false);
    // Clear form fields after closing alert
    setFormData({ name: "", email: "", phone: "", message: "" });
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F5F5F5",
        minHeight: "100vh",
        px: "100px",
        "@media (max-width: 833px)": {
          px: "16px",
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "1240px",
        }}
      >
        <Box
          sx={{
            margin: "0 auto",
            minHeight: "80vh",
            py: 8,
          }}
        >
          <Grid container spacing={4}>
            {/* Left Column - Contact Info */}
            <Grid item xs={12} md={6} sx={{ pr: "15px" }}>
              <Typography
                sx={{
                  fontSize: "48px",
                  "@media (max-width: 1270px)": {
                    fontSize: "36px",
                  },
                  fontWeight: "700",
                  color: "#333",
                  mb: 2,
                  lineHeight: 1.2,
                }}
              >
                Contact Us
              </Typography>

              <Typography
                sx={{
                  fontSize: "30px",
                  fontWeight: "600",
                  color: "#333",
                  mb: 2,
                }}
              >
                We're Here to Help
              </Typography>

              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#666",
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                At R3PLAY, we make buying and selling pre-loved toys simple, safe, and
                fun.
              </Typography>

              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#666",
                  mb: 3,
                  lineHeight: 1.6,
                  "@media (max-width: 1270px)": {
                    mb: 1,
                  },
                }}
              >
                If you have questions about your order, listing a toy, or anything
                else—our team is ready to assist.
              </Typography>

              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#333",
                  mb: 2,
                  "@media (max-width: 1270px)": {
                    display: "none",
                  },
                }}
              >
                Get in Touch
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  "@media (max-width: 1270px)": {
                    display: "none",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    backgroundColor: "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <EmailIcon sx={{ fontSize: "24px", color: "#666" }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#333",
                      mb: 0.5,
                    }}
                  >
                    Email Support
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#666",
                      mb: 0.5,
                    }}
                  >
                    Have a question or need help? Please send email to:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#EF5658",
                      fontWeight: "500",
                      mb: 0.5,
                    }}
                  >
                    letsplay@r3play.com
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Column - Contact Form */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                "@media (max-width: 1270px)": {
                  pt: 0,
                },
              }}
            >
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: "16px",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  p: 4,
                  "@media (max-width: 833px)": {
                    px: 2,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "30px",
                    fontWeight: "600",
                    color: "#333",
                    "@media (max-width: 833px)": {
                      fontSize: "24px",
                    },
                  }}
                >
                  We'd love to hear from you!
                </Typography>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#333",
                    mb: 3,
                  }}
                >
                  Let's get in touch
                </Typography>

                {/* Name Field */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={e => handleChange("name", e.target.value)}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "56px",
                      },
                      "& fieldset": {
                        borderColor: formErrors.name ? "#ef5658" : "rgba(0, 0, 0, 0.23)",
                      },
                      "& .MuiInputLabel-asterisk": {
                        color: "#ef5658 !important",
                      },
                      "& .MuiFormLabel-asterisk": {
                        color: "#ef5658 !important",
                      },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: formErrors.name ? "#ef5658" : "rgba(0, 0, 0, 0.23)",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#ef5658",
                          borderWidth: "1px",
                        },
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                        marginTop: "4px",
                        fontSize: "12px",
                      },
                    }}
                  />
                </Box>

                {/* Email Field */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    required
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={e => handleChange("email", e.target.value)}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "56px",
                      },
                      "& fieldset": {
                        borderColor: formErrors.email ? "#ef5658" : "rgba(0, 0, 0, 0.23)",
                      },
                      "& .MuiInputLabel-asterisk": {
                        color: "#ef5658 !important",
                      },
                      "& .MuiFormLabel-asterisk": {
                        color: "#ef5658 !important",
                      },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: formErrors.email ? "#ef5658" : "rgba(0, 0, 0, 0.23)",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#ef5658",
                          borderWidth: "1px",
                        },
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                        marginTop: "4px",
                        fontSize: "12px",
                      },
                    }}
                  />
                </Box>

                {/* Phone Field (Optional) */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={e => handleChange("phone", e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "56px",
                      },
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.23)",
                      },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.23)",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#ef5658",
                          borderWidth: "1px",
                        },
                    }}
                  />
                </Box>

                {/* Message Field */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Message"
                    required
                    multiline
                    rows={5}
                    placeholder="Enter your message"
                    value={formData.message}
                    onChange={e => handleChange("message", e.target.value)}
                    error={!!formErrors.message}
                    helperText={formErrors.message}
                    sx={{
                      "& fieldset": {
                        borderColor: formErrors.message
                          ? "#ef5658"
                          : "rgba(0, 0, 0, 0.23)",
                      },
                      "& .MuiInputLabel-asterisk": {
                        color: "#ef5658 !important",
                      },
                      "& .MuiFormLabel-asterisk": {
                        color: "#ef5658 !important",
                      },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: formErrors.message
                          ? "#ef5658"
                          : "rgba(0, 0, 0, 0.23)",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#ef5658",
                          borderWidth: "1px",
                        },
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                        marginTop: "4px",
                        fontSize: "12px",
                      },
                    }}
                  />
                </Box>

                {/* Submit Button */}
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                      backgroundColor: "#EF5658",
                      color: "white",
                      borderRadius: "4px",
                      py: 1.5,
                      px: 2,
                      height: "36px",
                      fontSize: "14px",
                      fontWeight: "medium",
                      textTransform: "uppercase",
                      minWidth: "160px",
                      "&:hover": {
                        backgroundColor: "#D33338",
                      },
                    }}
                  >
                    SEND MESSAGE
                  </Button>
                </Box>
              </Box>

              {/* Get in Touch - Mobile Only (Below Form) */}
              <Box
                sx={{
                  display: "none",
                  "@media (max-width: 1270px)": {
                    display: "block",
                    mt: 4,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#333",
                    mb: 2,
                  }}
                >
                  Get in Touch
                </Typography>

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      backgroundColor: "grey.200",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <EmailIcon sx={{ fontSize: "24px", color: "#666" }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#333",
                        mb: 0.5,
                      }}
                    >
                      Email Support
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        color: "#666",
                        mb: 0.5,
                      }}
                    >
                      Have a question or need help? Please send email to:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        color: "#EF5658",
                        fontWeight: "500",
                        mb: 0.5,
                      }}
                    >
                      letsplay@r3play.com
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Alert Popup */}
      <AlertPopup
        open={showAlert}
        onClose={handleAlertClose}
        title="Thank you for contacting us! We'll get back to you soon."
        confirmText="OK"
        cancelText=""
      />
    </Box>
  );
};

export default Contact;
